'use client'

import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  GET_ADVANCED_PRODUCT_DETAILS,
  UPDATE_ADVANCED_PRODUCT_PROFILES,
} from '../productQueries'
import { UPDATE_PRODUCT_CONTENT_BLOCKS, UPDATE_PRODUCT_FILES } from '../productMutations'
import { ContentBlocksManager } from '@/containers/ContentBlocks/ContentBlocksManager'
import { ContentBlocksManagerHandle } from '@/containers/ContentBlocks/types'
import { GET_ADMIN_SETTINGS } from '@/containers/Settings/adminSettingsQueries'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { Separator } from '@radix-ui/react-separator'
import { Divide } from 'lucide-react'
import { ProductFilesCard } from './ProductFilesCard'
import {
  PRODUCT_FILE_URL,
  deriveProductFileS3Key,
} from '@/components/shared/inputComponents/ProductFileRowEditor'

const formSchema = z.object({
  reference: z.string().optional(),
  advanced_product_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().min(1, 'Name is required'),
        long_product_name: z.string().optional(),
        original_product_name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
        meta_information: z.object({
          id: z.number().optional(),
          permalink: z.string().min(1, 'Permalink is required'),
          meta_title: z.string().optional(),
          meta_description: z.string().optional(),
        }),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
  product_files: z
    .array(
      z.object({
        _key: z.string().optional(),
        id: z.number().optional(),
        name: z.string().min(1, 'Name is required'),
        edition: z.string().optional().nullable(),
        url: z
          .string()
          .min(1, 'URL is required')
          .regex(
            PRODUCT_FILE_URL,
            'Must be a sftp-furnisystems.s3.amazonaws.com/vilmers/4_PRODUCT_CATALOGUES/…pdf or …/dominari/Catalogs/…pdf URL'
          ),
        suppressed: z.boolean().optional(),
      })
    )
    .optional(),
})

interface AdvancedProductFormProps {
  data: {
    id: string
    type: string
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function AdvancedProductForm({
  data,
  onDirtyChange,
  onRequestClose,
}: AdvancedProductFormProps) {
  const { allLanguages, languageId, language } = useLanguage()
  const id = Number(data?.id)
  const managerRef = useRef<ContentBlocksManagerHandle>(null)

  // `error` matters as much as `loading` here, and is the half that was
  // missing.
  //
  // `product_files` is a nonNull list whose `includeSuppressed` arg throws on a
  // non-staff or expired token, and a throw inside a nonNull field propagates
  // up and kills the WHOLE query -- so an expired admin session does not
  // degrade the files card, it leaves the entire product unloaded. Without
  // this, the form renders its blank defaults, looks perfectly editable, and
  // Save is enabled because `loading` is false.
  const {
    data: { findUniqueProductContainer: productData } = {},
    loading: loadingProductData,
    error: productDataError,
  } = useQuery(GET_ADVANCED_PRODUCT_DETAILS, {
    variables: {
      id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const { data: adminSettingsData } = useQuery(GET_ADMIN_SETTINGS)

  const adminSettings = adminSettingsData?.findManySetting || []
  const descriptionTranslateAction = adminSettings.find(
    setting => setting.name === 'admin_product_description_translate_action'
  )?.value
  const nameTranslateAction = adminSettings.find(
    setting => setting.name === 'admin_product_name_translate_action'
  )?.value

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      reference: '',
      advanced_product_profiles: allLanguages.map(lang => ({
        id: undefined,
        name: '',
        long_product_name: '',
        original_product_name: '',
        description: '',
        language: lang,
        meta_information: {
          id: undefined,
          permalink: '',
          meta_title: '',
          meta_description: '',
        },
      })),
      product_files: [] as z.infer<typeof formSchema>['product_files'],
    },
  })

  console.log(
    'form errors',
    form.formState.errors,
    form.formState.touchedFields
  )

  // Both of these refetch the product details query, and the order matters.
  //
  // `updateProductContentBlocks` fires its refetch BEFORE the file write below,
  // so the response it gets back cannot contain that write -- and when it lands
  // it re-runs the hydration effect, resetting the form to the PRE-SAVE file
  // list. The edit still reaches the server (onSubmit sends the validated
  // `values` snapshot, not form state), but the field visibly snaps back to the
  // old URL afterwards, and the next Save would then send the old URL and
  // silently undo the change.
  //
  // So: await the content-blocks refetch, so it cannot land mid-write or race
  // (two in-flight copies of the same query can be deduplicated, which would
  // hand the later one the earlier one's stale result), and give the file write
  // its own awaited refetch so the LAST thing the form hydrates from is server
  // state that includes it.
  const [updateProductContentBlocks] = useMutation(
    UPDATE_PRODUCT_CONTENT_BLOCKS,
    {
      refetchQueries: [
        'GetAdvancedProductDetails',
        'FindManyProductContainer',
      ],
      awaitRefetchQueries: true,
    }
  )

  const [updateProduct] = useMutation(UPDATE_ADVANCED_PRODUCT_PROFILES, {
    refetchQueries: ['FindManyProductContainer'],
  })

  const [updateProductFiles] = useMutation(UPDATE_PRODUCT_FILES, {
    refetchQueries: ['GetAdvancedProductDetails'],
    awaitRefetchQueries: true,
  })

  // `updateProductFiles` is a FULL REPLACE of the hand-attached rows: the array
  // it is sent IS the desired state, so an empty one deletes every file an
  // admin ever attached by hand. The ONLY thing that ever puts existing rows
  // into form state is the reset below, and that reset is skipped whenever the
  // product query did not produce usable data (an error, a null
  // `advanced_product`) -- in which case the `[]` default would be submitted as
  // "delete them all".
  //
  // Nothing else catches this. The zod field is `.optional()`, so an absent
  // list is valid; and gating Save on `loading` alone is exactly what failed in
  // the page-editor incident, because `loading` is false for a query that
  // errored.
  //
  // So the flag is set ONLY inside the reset that actually populated the list,
  // and it is stamped with the product id so it self-invalidates the moment a
  // different product is opened in this same component.
  const [filesHydratedForId, setFilesHydratedForId] = useState<number | null>(
    null,
  )
  const productFilesHydrated = !!id && filesHydratedForId === id

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      await updateProduct({
        variables: {
          id,
          reference: values.reference,
          advanced_product_profiles: values.advanced_product_profiles.map(p => ({
            id: p.id,
            name: p.name,
            long_product_name: p.long_product_name,
            original_product_name: p.original_product_name,
            description: p.description,
            language: p.language,
            meta_information: {
              id: p.meta_information?.id,
              permalink: p.meta_information.permalink,
              meta_title: p.meta_information.meta_title || '',
              meta_description: p.meta_information.meta_description || '',
            },
          })),
        },
      })
    } catch {
      toast.error('Failed to update product')
      return
    }

    if (id) {
      try {
        await updateProductContentBlocks({
          variables: {
            id,
            content_blocks: managerRef.current?.getBlockIds() ?? [],
          },
        })
        await managerRef.current?.commitArrangement()
      } catch {
        toast.error('Failed to update product content blocks')
        return
      }

      // Refuse to send a list this form never loaded. See the comment on
      // `filesHydratedForId`: sending the untouched `[]` default here would
      // delete every hand-attached file on the product. Everything above has
      // already been saved, so the message has to be explicit that the files
      // are the part that did not go through.
      if (!productFilesHydrated) {
        toast.error(
          'Product saved, but catalogue files could not be loaded and were left unchanged. Reload the product before editing its files.',
        )
        return
      }

      // Full-replace of the hand-attached (erp_managed: false) files only —
      // ERP rows are never read from form state, so they can't be sent here.
      // Two rows that differ ONLY by `?etag=` still derive the same key, so
      // they collide -- the server rejects the whole call with
      // "Duplicate product file: <key>". Caught here first so the message can
      // name the row and the non-obvious reason rather than showing a raw key.
      const productFiles = values.product_files ?? []
      const seenKeys = new Map<string, string>()
      for (const file of productFiles) {
        const s3_key = deriveProductFileS3Key(file.url)
        const clashesWith = seenKeys.get(s3_key)
        if (clashesWith !== undefined) {
          toast.error(
            `"${file.name}" and "${clashesWith}" point at the same file (${s3_key}). ` +
              `Two rows can't share one PDF — a different ?etag= is still the same file. ` +
              `Remove one of them.`
          )
          return
        }
        seenKeys.set(s3_key, file.name)
      }
      try {
        await updateProductFiles({
          variables: {
            productContainerId: id,
            files: productFiles.map(file => ({
              name: file.name,
              url: file.url,
              edition: file.edition || null,
            })),
          },
        })
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to update product files')
        return
      }
    }

    toast.success('Product updated successfully')
    const currentValues = form.getValues()
    form.reset(currentValues)
  }

  useEffect(() => {
    if (productData?.advanced_product?.advanced_product_profiles) {
      const existingProfiles =
        productData.advanced_product.advanced_product_profiles || []

      const formValues = {
        reference: productData.reference ?? '',
        advanced_product_profiles: allLanguages.map(lang => {
          // Find existing data for this language
          const existingProfile = existingProfiles.find(
            p => p.language === lang
          )

          if (existingProfile) {
            // Use existing data
            return {
              id: existingProfile.id,
              name: existingProfile.name,
              long_product_name: existingProfile.long_product_name ?? '',
              original_product_name:
                existingProfile.original_product_name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
              meta_information: {
                id: existingProfile.meta_information?.id,
                permalink: existingProfile.meta_information?.permalink ?? '',
                meta_title: existingProfile.meta_information?.meta_title ?? '',
                meta_description:
                  existingProfile.meta_information?.meta_description ?? '',
              },
            }
          } else {
            // Create new empty profile for this language
            return {
              id: undefined,
              name: '',
              long_product_name: '',
              original_product_name: '',
              description: '',
              language: lang,
              meta_information: {
                id: undefined,
                permalink: '',
                meta_title: '',
                meta_description: '',
              },
            }
          }
        }),
        product_files: [...(productData.product_files ?? [])]
          .filter(file => !file.erp_managed)
          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
          .map(file => ({
            _key: crypto.randomUUID(),
            id: file.id,
            name: file.name,
            edition: file.edition ?? '',
            url: file.url,
            suppressed: file.suppressed,
          })),
      }
      form.reset(formValues)
      // Set ONLY here, and only after `product_files` has actually been written
      // into form state from a server response. This is the single fact
      // onSubmit trusts before it is allowed to full-replace the file list.
      setFilesHydratedForId(id)
    }
  }, [productData, form.reset, allLanguages, id])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  // Helper function to create URL-friendly permalink
  const createPermalink = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim()
      .substring(0, 50) // Limit length
  }

  // Handler to sync field values across all languages when setting is "make_all_same"
  const handleFieldChange = (
    fieldName: 'name' | 'description',
    value: string
  ) => {
    const translateAction =
      fieldName === 'name' ? nameTranslateAction : descriptionTranslateAction

    if (translateAction === 'make_all_same') {
      // Update all language profiles with the same value
      allLanguages.forEach((_, langIndex) => {
        form.setValue(
          `advanced_product_profiles.${langIndex}.${fieldName}`,
          value,
          {
            shouldDirty: true,
            shouldTouch: true,
          }
        )

        // If description is being set, also set default values for name and permalink if empty
        if (fieldName === 'description' && value.trim()) {
          const currentName = form.getValues(
            `advanced_product_profiles.${langIndex}.name`
          )
          const currentPermalink = form.getValues(
            `advanced_product_profiles.${langIndex}.meta_information.permalink`
          )

          // Set default name if empty (take from default language)
          if (!currentName || currentName.trim() === '') {
            const defaultLanguageName = form.getValues(
              `advanced_product_profiles.0.name`
            )
            if (defaultLanguageName && defaultLanguageName.trim() !== '') {
              form.setValue(
                `advanced_product_profiles.${langIndex}.name`,
                defaultLanguageName,
                {
                  shouldDirty: true,
                  shouldTouch: true,
                }
              )
            }
          }

          // Set default permalink if empty (take from default language)
          if (!currentPermalink || currentPermalink.trim() === '') {
            const defaultLanguagePermalink = form.getValues(
              `advanced_product_profiles.0.meta_information.permalink`
            )
            if (
              defaultLanguagePermalink &&
              defaultLanguagePermalink.trim() !== ''
            ) {
              form.setValue(
                `advanced_product_profiles.${langIndex}.meta_information.permalink`,
                defaultLanguagePermalink,
                {
                  shouldDirty: true,
                  shouldTouch: true,
                }
              )
            }
          }
        }
      })
    }
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Advanced Product</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            {/* An expired or non-staff token makes `product_files` throw, and
                because it is a nonNull list that kills the whole product query
                -- so the fields below are blank DEFAULTS, not the product. Say
                so, instead of presenting an editable-looking empty form. */}
            {productDataError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                <p className="font-medium">This product could not be loaded.</p>
                <p>
                  Your session may have expired — sign in again and reopen it.
                  The fields below are empty defaults, not this product&apos;s
                  data, so saving is disabled and nothing here can overwrite it.
                </p>
              </div>
            )}
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Product Name"
                description="The name of your product that will be displayed to customers."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Enter product name"
                        {...field}
                        onChange={e => {
                          field.onChange(e)
                          handleFieldChange('name', e.target.value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Long Product Name"
                description="A longer, more descriptive name for your product."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.long_product_name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter long product name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Original Product Name"
                description="The original name of the product, useful for reference."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.original_product_name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Enter original product name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                description="Detailed description of your product including key features and benefits."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Enter product description"
                        className="min-h-[100px] resize-none"
                        {...field}
                        onChange={e => {
                          field.onChange(e)
                          handleFieldChange('description', e.target.value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Permalink"
                description="URL-friendly version of the product name."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.meta_information.permalink`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter permalink" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Title"
                description="Title used for SEO purposes."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.meta_information.meta_title`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter meta title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Description"
                description="Description used for SEO purposes."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.meta_information.meta_description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Enter meta description"
                        className="min-h-[100px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator className="my-4" />

              <InputFieldContainer
                label="Reference"
                description="A reference identifier for the product."
                control={form.control}
                name="reference"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter product reference" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {id ? (
                <div className="border-t pt-4 mt-4">
                  <ContentBlocksManager
                    ref={managerRef}
                    owner={{ kind: 'product', id }}
                    initialBlocks={productData?.content_blocks ?? []}
                    enableInlineCreate={false}
                    enableEditExisting
                    enablePermanentDelete={false}
                  />
                </div>
              ) : null}

              {/* Rendered only once the list has actually been loaded. An
                  editor bound to an empty list the admin never saw is a trap:
                  every row they add would look saved while onSubmit refuses to
                  send the list at all. */}
              {id ? (
                <div className="border-t pt-4 mt-4">
                  {productFilesHydrated ? (
                    <ProductFilesCard
                      form={form}
                      files={productData?.product_files ?? []}
                      refetchQueries={['GetAdvancedProductDetails']}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {loadingProductData
                        ? 'Loading catalogue files…'
                        : 'Catalogue files could not be loaded. Reload the product to edit them — saving now will leave the existing files unchanged.'}
                    </p>
                  )}
                </div>
              ) : null}

              <div className="flex justify-end gap-2">
                <Button
                  type="submit"
                  disabled={loadingProductData || !!productDataError}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
